import { useState, useEffect } from "react";
const API_URL = process.env.NEXT_PUBLIC_API_URL || "";

interface BrandPriceStatsResult {
  minPrice: number;
  maxPrice: number;
  avgPrice: number;
  medianPrice: number;
  q1Price: number;
  q3Price: number;
  totalCount: number;
}

interface TopVendor {
  id: number;
  name: string;
  region: string;
  listingCount: number;
}

interface TopRegion {
  region: string;
  listingCount: number;
}

interface UseBrandPriceStatsResult {
  stats: BrandPriceStatsResult | null;
  topMarketplaces: TopVendor[];
  topIndependentVendors: TopVendor[];
  topRegions: TopRegion[];
  loading: boolean;
}

/**
 * Fetches IQR-filtered price stats for a brand from DuckDB.
 * When itemtype is provided, stats are scoped to that category.
 */
export function useBrandPriceStats(
  brandId: number | null | undefined,
  itemtype?: string | null
): UseBrandPriceStatsResult {
  const [stats, setStats] = useState<BrandPriceStatsResult | null>(null);
  const [topMarketplaces, setTopMarketplaces] = useState<TopVendor[]>([]);
  const [topIndependentVendors, setTopIndependentVendors] = useState<TopVendor[]>([]);
  const [topRegions, setTopRegions] = useState<TopRegion[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!brandId) {
      setStats(null);
      setTopMarketplaces([]);
      setTopIndependentVendors([]);
      setTopRegions([]);
      return;
    }

    let cancelled = false;
    setLoading(true);

    const params = new URLSearchParams();
    if (itemtype) params.set("itemtype", itemtype);

    const url = `${API_URL}/api/v2/analytics/brands/${brandId}${params.size > 0 ? `?${params}` : ""}`;

    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch brand price stats");
        return res.json();
      })
      .then((data) => {
        if (!cancelled) {
          const ps = data.data?.price_statistics;
          if (ps) {
            setStats({
              minPrice: Number(ps.min_price),
              maxPrice: Number(ps.max_price),
              avgPrice: Number(ps.mean_price),
              medianPrice: Number(ps.median),
              q1Price: Number(ps.lower_quartile),
              q3Price: Number(ps.upper_quartile),
              totalCount: ps.sample_size,
            });
          } else {
            setStats(null);
          }

          const mapVendor = (v: {
            id: number;
            name: string;
            region: string;
            listing_count: number;
          }) => ({
            id: v.id,
            name: v.name,
            region: v.region,
            listingCount: v.listing_count,
          });

          setTopMarketplaces((data.data?.top_marketplaces ?? []).map(mapVendor));
          setTopIndependentVendors((data.data?.top_independent_vendors ?? []).map(mapVendor));
          setTopRegions(
            (data.data?.top_regions ?? []).map((r: { region: string; listing_count: number }) => ({
              region: r.region,
              listingCount: r.listing_count,
            }))
          );

          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStats(null);
          setTopMarketplaces([]);
          setTopIndependentVendors([]);
          setTopRegions([]);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [brandId, itemtype]);

  return { stats, topMarketplaces, topIndependentVendors, topRegions, loading };
}
